
import { useState } from 'react';

export default function PeripheralSystems() {
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleInquirySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d3c8ocsef3sotc9j5t8o', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        alert('お問い合わせありがとうございます。担当者より3営業日以内にご連絡いたします。');
        form.reset();
        setShowInquiryForm(false);
      }
    } catch (error) {
      alert('送信に失敗しました。お電話でのお問い合わせもお受けしております。');
    }
  };

  const peripheralEquipment = [
    {
      id: 'basic',
      category: '基本周辺機器',
      description: 'パレタイザー運用に必要不可欠な基本機器',
      equipment: [
        {
          name: 'パレット',
          description: '物流に用いる荷物を載せるための荷役台です。',
          specifications: ['サイズ: 1100×1100mm（標準）', '材質: 木製・プラスチック製', '耐荷重: 1000-2000kg'],
          applications: '全業界共通',
          image: 'https://readdy.ai/api/search-image?query=Industrial%20wooden%20and%20plastic%20pallets%20stacked%20in%20warehouse%2C%20standard%20logistics%20equipment%2C%20various%20pallet%20types%20for%20different%20industries&width=300&height=200&seq=pallets&orientation=landscape'
        },
        {
          name: 'パレットディスペンサー',
          description: 'パレットをコンベアラインに一枚ずつ供給していく装置です。',
          specifications: ['供給能力: 10-20枚/分', '積載枚数: 20-50枚', '自動高さ調整機能'],
          applications: '自動化ライン',
          image: 'https://readdy.ai/api/search-image?query=Automatic%20pallet%20dispenser%20machine%20feeding%20pallets%20to%20conveyor%20line%2C%20industrial%20automation%20equipment%2C%20precise%20pallet%20supply%20system&width=300&height=200&seq=pallet-dispenser&orientation=landscape'
        },
        {
          name: 'コンベヤ',
          description: '前工程とパレタイザーをつなぐときに使います。',
          specifications: ['搬送速度: 10-60m/分', '耐荷重: 50-500kg', 'ローラー・ベルト式'],
          applications: '製造・物流ライン',
          image: 'https://readdy.ai/api/search-image?query=Industrial%20conveyor%20belt%20system%20connecting%20production%20line%20to%20palletizer%2C%20automated%20material%20handling%2C%20modern%20factory%20equipment&width=300&height=200&seq=conveyor&orientation=landscape'
        }
      ]
    },
    {
      id: 'packaging',
      category: '包装・梱包機器',
      description: '積み付け後の製品保護と輸送準備',
      equipment: [
        {
          name: 'ストレッチ包装機',
          description: 'パレットに積まれた製品にストレッチフィルムを巻き付ける装置です。',
          specifications: ['巻き付け速度: 15-30回転/分', 'フィルム幅: 500mm', '自動カット機能'],
          applications: '出荷準備・輸送',
          image: 'https://readdy.ai/api/search-image?query=Automatic%20stretch%20wrapping%20machine%20applying%20plastic%20film%20to%20palletized%20goods%2C%20industrial%20packaging%20equipment%2C%20warehouse%20automation&width=300&height=200&seq=stretch-wrapper&orientation=landscape'
        },
        {
          name: 'ストレッチフィルム',
          description: 'ポリエチレン樹脂などを原料とした梱包用フィルムです。',
          specifications: ['厚み: 15-25μm', '幅: 500mm', '透明・着色タイプ'],
          applications: '製品保護・固定',
          image: 'https://readdy.ai/api/search-image?query=Industrial%20stretch%20film%20rolls%20and%20wrapped%20palletized%20products%2C%20transparent%20plastic%20packaging%20material%2C%20logistics%20protection%20system&width=300&height=200&seq=stretch-film&orientation=landscape'
        }
      ]
    },
    {
      id: 'transport',
      category: '搬送・運搬機器',
      description: '完成品の効率的な移動と保管',
      equipment: [
        {
          name: 'ハンドパレットトラック',
          description: '積みつけられたパレットを持ち上げる、運搬する際に省力化が可能です。',
          specifications: ['積載能力: 2000-3000kg', 'フォーク長: 1150mm', '手動・電動タイプ'],
          applications: '短距離搬送',
          image: 'https://readdy.ai/api/search-image?query=Manual%20and%20electric%20pallet%20trucks%20in%20warehouse%2C%20material%20handling%20equipment%2C%20workers%20using%20pallet%20jacks%20for%20transportation&width=300&height=200&seq=pallet-truck&orientation=landscape'
        },
        {
          name: 'フォークリフト',
          description: '油圧を利用して昇降および傾斜が可能な荷役用のつめを車体前面に備えた荷役自動車です。',
          specifications: ['積載能力: 1000-5000kg', '最大揚高: 3-6m', 'エンジン・バッテリー式'],
          applications: '高所作業・長距離搬送',
          image: 'https://readdy.ai/api/search-image?query=Industrial%20forklift%20trucks%20operating%20in%20warehouse%2C%20heavy-duty%20material%20handling%20vehicles%2C%20professional%20logistics%20equipment&width=300&height=200&seq=forklift&orientation=landscape'
        },
        {
          name: 'AGV（無人搬送車）',
          description: 'AGVは産業用途で多く使用される搬送機です。人間が運転操作なしで自動走行が可能です。',
          specifications: ['積載能力: 500-2000kg', '走行速度: 30-60m/分', 'レーザー・磁気誘導'],
          applications: '無人化・自動化',
          image: 'https://readdy.ai/api/search-image?query=Automated%20guided%20vehicles%20AGV%20in%20modern%20warehouse%2C%20unmanned%20transport%20system%2C%20autonomous%20material%20handling%20robots&width=300&height=200&seq=agv&orientation=landscape'
        }
      ]
    },
    {
      id: 'advanced',
      category: '高度化機器',
      description: '生産性向上と品質管理の高度化',
      equipment: [
        {
          name: '3Dビジョンカメラ',
          description: '物体を3次元的に認識する計測器です。ロボットがバラ積み状態のワークを正確に把持できます。',
          specifications: ['測定精度: ±0.1mm', '測定範囲: 500×500mm', 'リアルタイム処理'],
          applications: 'ピッキング・品質検査',
          image: 'https://readdy.ai/api/search-image?query=3D%20vision%20camera%20system%20for%20industrial%20robotics%2C%20advanced%20optical%20measurement%20equipment%2C%20automated%20quality%20inspection%20technology&width=300&height=200&seq=3d-vision&orientation=landscape'
        },
        {
          name: '段積み機',
          description: '段ボール箱を縦に積み重ねる装置です。積み重ねた段ボールをパレタイザーで2個つかみ、3個つかみをすることで処理能力を上げられます。',
          specifications: ['積み重ね段数: 2-5段', '処理能力: 200-400ケース/時', '自動位置調整'],
          applications: '処理能力向上',
          image: 'https://readdy.ai/api/search-image?query=Automatic%20case%20stacking%20machine%20creating%20multi-layer%20cardboard%20arrangements%2C%20industrial%20packaging%20automation%2C%20efficiency%20improvement%20equipment&width=300&height=200&seq=case-stacker&orientation=landscape'
        }
      ]
    }
  ];

  const systemIntegration = [
    {
      title: 'WMS連携システム',
      description: '倉庫管理システムとの連携により、在庫管理と出荷指示を自動化',
      benefits: ['リアルタイム在庫管理', '出荷指示の自動化', 'トレーサビリティ向上'],
      image: 'https://readdy.ai/api/search-image?query=Warehouse%20management%20system%20WMS%20interface%20on%20computer%20screens%2C%20integrated%20logistics%20software%2C%20real-time%20inventory%20tracking%20dashboard&width=400&height=300&seq=wms-system&orientation=landscape'
    },
    {
      title: 'IoT監視システム',
      description: '機器の稼働状況をリアルタイムで監視し、予防保全を実現',
      benefits: ['故障予知', '稼働率向上', 'メンテナンス最適化'],
      image: 'https://readdy.ai/api/search-image?query=IoT%20monitoring%20dashboard%20showing%20industrial%20equipment%20status%2C%20real-time%20data%20visualization%2C%20predictive%20maintenance%20system%20interface&width=400&height=300&seq=iot-monitoring&orientation=landscape'
    },
    {
      title: 'エネルギー管理システム',
      description: '電力消費量の最適化により、運用コストを削減',
      benefits: ['省エネ効果', 'コスト削減', 'CO2排出量削減'],
      image: 'https://readdy.ai/api/search-image?query=Energy%20management%20system%20dashboard%20showing%20power%20consumption%20data%2C%20industrial%20energy%20efficiency%20monitoring%2C%20green%20technology%20interface&width=400&height=300&seq=energy-management&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-800 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <a href="/" className="text-2xl font-bold hover:text-orange-400 cursor-pointer transition-colors" style={{ fontFamily: '"Arial", "Helvetica", sans-serif' }}>パレタイザーナビ</a>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <a href="/palletizer-basics" className="hover:text-orange-400 transition-colors cursor-pointer">パレタイザーとは</a>
            <a href="/selection-guide" className="hover:text-orange-400 transition-colors cursor-pointer">機種選定ガイド</a>
            <a href="/decision-criteria" className="hover:text-orange-400 transition-colors cursor-pointer">導入判断基準</a>
            <a href="/case-studies" className="hover:text-orange-400 transition-colors cursor-pointer">導入事例</a>
            <a href="/manufacturers-products" className="hover:text-orange-400 transition-colors cursor-pointer">メーカー・製品</a>
            <a href="/peripheral-systems" className="hover:text-orange-400 transition-colors cursor-pointer">周辺機器</a>
            <a href="/alternatives-comparison" className="hover:text-orange-400 transition-colors cursor-pointer">代替案・比較</a>
          </nav>

          <div className="flex items-center space-x-6">
            <button 
              onClick={() => setShowInquiryForm(true)}
              className="bg-orange-500 hover:bg-orange-600 px-6 py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              見積依頼・お問い合わせ
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white cursor-pointer"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-700">
            <nav className="flex flex-col space-y-4 mt-4">
              <a href="/palletizer-basics" className="hover:text-orange-400 transition-colors cursor-pointer">パレタイザーとは</a>
              <a href="/selection-guide" className="hover:text-orange-400 transition-colors cursor-pointer">機種選定ガイド</a>
              <a href="/decision-criteria" className="hover:text-orange-400 transition-colors cursor-pointer">導入判断基準</a>
              <a href="/case-studies" className="hover:text-orange-400 transition-colors cursor-pointer">導入事例</a>
              <a href="/manufacturers-products" className="hover:text-orange-400 transition-colors cursor-pointer">メーカー・製品</a>
              <a href="/peripheral-systems" className="hover:text-orange-400 transition-colors cursor-pointer">周辺機器</a>
              <a href="/alternatives-comparison" className="hover:text-orange-400 transition-colors cursor-pointer">代替案・比較</a>
              <button 
                onClick={() => {
                  setShowInquiryForm(true);
                  setMobileMenuOpen(false);
                }}
                className="bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors text-left"
              >
                見積依頼・お問い合わせ
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center space-x-2 text-sm text-slate-600">
            <a href="/" className="hover:text-blue-600 cursor-pointer">ホーム</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-slate-800 font-semibold">周辺機器・システム</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-800 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6">周辺機器・システム</h1>
          <p className="text-xl text-teal-100 mb-8">トータルソリューションで最大効果を実現</p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-6">パレタイザーシステムの全体像</h2>
              <p className="text-lg text-slate-600 mb-6">
                パレタイザーは単体では機能しません。前工程からの製品供給、積み付け後の包装・搬送まで、
                一連のシステムとして設計することで、真の自動化効果を発揮します。
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">トータルシステム設計</h3>
                    <p className="text-slate-600">前後工程を含めた最適なライン構成</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">周辺機器の最適選定</h3>
                    <p className="text-slate-600">用途に応じた機器の組み合わせ提案</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">統合制御システム</h3>
                    <p className="text-slate-600">全機器の連携による効率的な運用</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Complete%20palletizing%20system%20layout%20showing%20conveyor%20lines%2C%20palletizer%20robot%2C%20stretch%20wrapper%2C%20and%20peripheral%20equipment%20in%20modern%20warehouse%2C%20integrated%20automation%20solution&width=600&height=400&seq=system-overview&orientation=landscape"
                alt="パレタイザーシステム全体像"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Categories */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">周辺機器カテゴリー</h2>
          
          {/* Category Navigation */}
          <div className="flex justify-center mb-12">
            <div className="flex flex-wrap gap-4">
              {peripheralEquipment.map((category, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                  className={`px-6 py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors ${
                    selectedCategory === category.id 
                      ? 'bg-teal-600 text-white' 
                      : 'bg-white text-teal-600 border-2 border-teal-600 hover:bg-teal-50'
                  }`}
                >
                  {category.category}
                </button>
              ))}
            </div>
          </div>

          {/* Equipment Details */}
          {peripheralEquipment.map((category, categoryIndex) => (
            <div 
              key={categoryIndex} 
              className={`${selectedCategory && selectedCategory !== category.id ? 'hidden' : ''} mb-16`}
            >
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-slate-800 mb-4">{category.category}</h3>
                <p className="text-lg text-slate-600">{category.description}</p>
              </div>
              
              <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-8">
                {category.equipment.map((equipment, equipmentIndex) => (
                  <div key={equipmentIndex} className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <img 
                      src={equipment.image}
                      alt={equipment.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-6">
                      <h4 className="text-xl font-bold text-slate-800 mb-3">{equipment.name}</h4>
                      <p className="text-slate-600 mb-4">{equipment.description}</p>
                      
                      <div className="mb-4">
                        <h5 className="font-semibold text-slate-800 mb-2">主な仕様</h5>
                        <ul className="space-y-1">
                          {equipment.specifications.map((spec, specIndex) => (
                            <li key={specIndex} className="flex items-start">
                              <i className="ri-check-line text-teal-600 mr-2 mt-1 text-sm"></i>
                              <span className="text-sm text-slate-600">{spec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="bg-teal-50 p-3 rounded-lg">
                        <span className="text-sm font-semibold text-teal-800">適用分野: </span>
                        <span className="text-sm text-teal-600">{equipment.applications}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* System Integration */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">システム統合ソリューション</h2>
          <div className="grid lg:grid-cols-3 gap-8">
            {systemIntegration.map((system, index) => (
              <div key={index} className="bg-slate-50 rounded-lg shadow-lg overflow-hidden">
                <img 
                  src={system.image}
                  alt={system.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">{system.title}</h3>
                  <p className="text-slate-600 mb-6">{system.description}</p>
                  
                  <div>
                    <h4 className="font-bold text-slate-800 mb-3">主な効果</h4>
                    <ul className="space-y-2">
                      {system.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="flex items-start">
                          <i className="ri-star-line text-teal-600 mr-2 mt-1"></i>
                          <span className="text-slate-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Breakdown */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">システム導入費用の構成</h2>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-slate-800 mb-6">費用構成の目安</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-teal-50 rounded-lg">
                    <span className="font-semibold">パレタイザー本体</span>
                    <span className="text-xl font-bold text-teal-600">50-60%</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                    <span className="font-semibold">周辺機器</span>
                    <span className="text-xl font-bold text-blue-600">20-25%</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                    <span className="font-semibold">設置工事</span>
                    <span className="text-xl font-bold text-green-600">15-20%</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                    <span className="font-semibold">システム統合</span>
                    <span className="text-xl font-bold text-purple-600">5-10%</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-800 mb-6">周辺機器の価格帯</h3>
              <div className="space-y-4">
                {[
                  { item: 'パレットディスペンサー', price: '200-400万円' },
                  { item: 'ストレッチ包装機', price: '300-600万円' },
                  { item: 'コンベヤシステム', price: '100-300万円' },
                  { item: '3Dビジョンカメラ', price: '150-300万円' },
                  { item: 'AGV（無人搬送車）', price: '500-1000万円' },
                  { item: 'WMS連携システム', price: '100-200万円' }
                ].map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg border border-slate-200">
                    <span className="font-semibold text-slate-800">{item.item}</span>
                    <span className="text-slate-600">{item.price}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <i className="ri-information-line mr-2"></i>
                  価格は仕様・規模により変動します。詳細はお問い合わせください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance & Support */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">保守・メンテナンスサービス</h2>
          <div className="grid lg:grid-cols-4 gap-8">
            {[
              {
                title: '定期点検',
                description: '月次・年次の定期点検により故障を未然に防止',
                icon: 'ri-calendar-check-line',
                features: ['月次点検', '年次オーバーホール', '消耗品交換']
              },
              {
                title: '緊急対応',
                description: '24時間365日の緊急対応体制',
                icon: 'ri-24-hours-line',
                features: ['24時間受付', '緊急出動', '代替機貸出']
              },
              {
                title: '予防保全',
                description: 'IoT監視による予防保全サービス',
                icon: 'ri-shield-check-line',
                features: ['遠隔監視', '故障予知', '最適化提案']
              },
              {
                title: '技術サポート',
                description: '操作研修・技術指導サービス',
                icon: 'ri-graduation-cap-line',
                features: ['操作研修', '安全教育', '技術相談']
              }
            ].map((service, index) => (
              <div key={index} className="bg-slate-50 p-8 rounded-lg text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-4">{service.title}</h3>
                <p className="text-slate-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center justify-center">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-8">最適なシステム構成をご提案</h2>
          <p className="text-xl mb-8">お客様の現場に最適な周辺機器とシステム統合をトータルでサポートいたします</p>
          <div className="flex justify-center space-x-6">
            <a 
              href="/case-studies"
              className="bg-white text-teal-600 hover:bg-slate-100 px-10 py-4 rounded-lg text-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              導入事例を見る
            </a>
            <button 
              onClick={() => setShowInquiryForm(true)}
              className="bg-orange-500 hover:bg-orange-600 px-10 py-4 rounded-lg text-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              システム相談をする
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Arial", "Helvetica", sans-serif' }}>パレタイザーナビ</h3>
              <p className="text-slate-300 mb-4">パレタイザー専門企業として、製造業・物流業の自動化をサポートします。</p>
              <div className="flex space-x-4">
                <i className="ri-phone-line text-orange-400"></i>
                <span>03-1234-5678</span>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">サービス</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">機械式パレタイザー</a></li>
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">多関節ロボット</a></li>
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">直交ロボット</a></li>
                <li><a href="/peripheral-systems" className="hover:text-orange-400 cursor-pointer">保守・メンテナンス</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">詳細情報</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="/palletizer-basics" className="hover:text-orange-400 cursor-pointer">パレタイザーとは</a></li>
                <li><a href="/decision-criteria" className="hover:text-orange-400 cursor-pointer">導入判断基準</a></li>
                <li><a href="/case-studies" className="hover:text-orange-400 cursor-pointer">導入事例</a></li>
                <li><a href="/manufacturers-products" className="hover:text-orange-400 cursor-pointer">メーカー・製品</a></li>
                <li><a href="/peripheral-systems" className="hover:text-orange-400 cursor-pointer">周辺機器・システム</a></li>
                <li><a href="/alternatives-comparison" className="hover:text-orange-400 cursor-pointer">代替案・比較</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">会社情報</h4>
              <ul className="space-y-2 text-slate-300">
                <li>〒174-0000 東京都板橋区</li>
                <li>設立：1985年</li>
                <li>従業員数：120名</li>
                <li>ISO9001認証取得</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2024 パレタイザーナビ. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-orange-400 hover:text-orange-300 ml-2">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Inquiry Form Modal */}
      {showInquiryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-slate-800">システム構成のお問い合わせ</h3>
                <button 
                  onClick={() => setShowInquiryForm(false)}
                  className="text-slate-500 hover:text-slate-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form id="peripheral-systems-form" data-readdy-form onSubmit={handleInquirySubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">会社名 *</label>
                    <input 
                      type="text" 
                      name="company" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">お名前 *</label>
                    <input 
                      type="text" 
                      name="name" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">電話番号 *</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">メールアドレス *</label>
                    <input 
                      type="email" 
                      name="email" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">興味のある周辺機器</label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {['パレットディスペンサー', 'ストレッチ包装機', 'コンベヤシステム', '3Dビジョンカメラ', 'AGV', 'WMS連携システム'].map((equipment, index) => (
                      <label key={index} className="flex items-center">
                        <input type="checkbox" name="interested_equipment" value={equipment} className="mr-2" />
                        {equipment}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">現在の課題・要望</label>
                  <textarea 
                    name="system_requirements" 
                    rows={4} 
                    maxLength={500}
                    className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="現在のライン構成や改善したい点をお聞かせください（500文字以内）"
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
                >
                  送信する
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
